// 🤖 Assistant Bubble - Burbuja flotante del asistente virtual
import { useState } from 'react';
import i18n from '@/lib/i18n';
import { motion, AnimatePresence } from 'framer-motion';
import { MessageCircle, X, Sparkles } from 'lucide-react';
import { cn } from '@/lib/utils';

interface AssistantBubbleProps {
  isOpen: boolean;
  onToggle: () => void;
  notificationCount?: number;
}

export function AssistantBubble({
  isOpen,
  onToggle,
  notificationCount = 0,
}: AssistantBubbleProps) {
  const [isHovered, setIsHovered] = useState(false);

  return (
    <div data-asistente className="fixed right-6 z-50" style={{ bottom: 'calc(1.5rem + var(--alto-barra-app, 0px))' }}>
      {/* Burbuja Principal */}
      <motion.button
        onClick={onToggle}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        className={cn(
          'relative flex items-center justify-center w-16 h-16 rounded-full shadow-2xl transition-all duration-300',
          'bg-gradient-to-br from-purple-500 to-blue-600 hover:from-purple-600 hover:to-blue-700',
          'focus:outline-none focus:ring-4 focus:ring-purple-500/50',
          isOpen && 'bg-gradient-to-br from-red-500 to-pink-600'
        )}
        whileHover={{ scale: 1.1 }}
        whileTap={{ scale: 0.95 }}
        animate={{
          rotate: isOpen ? 90 : 0,
        }}
        transition={{ type: 'spring', stiffness: 300, damping: 20 }}
      >
        {/* Ondas animadas cuando está cerrado */}
        {!isOpen && (
          <>
            <motion.span
              className="absolute inset-0 rounded-full bg-purple-400"
              animate={{
                scale: [1, 1.5, 1.5],
                opacity: [0.5, 0, 0],
              }}
              transition={{
                duration: 2,
                repeat: Infinity,
                ease: 'easeOut',
              }}
            />
            <motion.span
              className="absolute inset-0 rounded-full bg-blue-400"
              animate={{
                scale: [1, 1.8, 1.8],
                opacity: [0.3, 0, 0],
              }}
              transition={{
                duration: 2,
                repeat: Infinity,
                ease: 'easeOut',
                delay: 0.5,
              }}
            />
          </>
        )}

        {/* Icono */}
        <AnimatePresence mode="wait">
          {isOpen ? (
            <motion.div
              key="close"
              initial={{ rotate: -90, opacity: 0 }}
              animate={{ rotate: 0, opacity: 1 }}
              exit={{ rotate: 90, opacity: 0 }}
              transition={{ duration: 0.2 }}
            >
              <X className="h-7 w-7 text-white" />
            </motion.div>
          ) : (
            <motion.div
              key="open"
              initial={{ rotate: 90, opacity: 0 }}
              animate={{ rotate: 0, opacity: 1 }}
              exit={{ rotate: -90, opacity: 0 }}
              transition={{ duration: 0.2 }}
              className="relative"
            >
              <MessageCircle className="h-7 w-7 text-white" />
              {/* Sparkle animado */}
              <motion.div
                className="absolute -top-1 -right-1"
                animate={{
                  scale: [1, 1.2, 1],
                  rotate: [0, 180, 360],
                }}
                transition={{
                  duration: 3,
                  repeat: Infinity,
                  ease: 'easeInOut',
                }}
              >
                <Sparkles className="h-3 w-3 text-yellow-300" />
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Badge de notificaciones */}
        {notificationCount > 0 && !isOpen && (
          <motion.span
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            className="absolute -top-1 -right-1 flex items-center justify-center w-6 h-6 text-xs font-bold text-white bg-red-500 rounded-full border-2 border-white"
          >
            {notificationCount > 9 ? '9+' : notificationCount}
          </motion.span>
        )}
      </motion.button>

      {/* Tooltip cuando hace hover */}
      <AnimatePresence>
        {isHovered && !isOpen && (
          <motion.div
            initial={{ opacity: 0, x: 10 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: 10 }}
            className="absolute right-20 top-1/2 -translate-y-1/2 px-4 py-2 bg-gray-900 text-white text-sm rounded-lg shadow-lg whitespace-nowrap"
          >
            ¿Necesitas ayuda? 🤖
            <div className="absolute right-[-6px] top-1/2 -translate-y-1/2 w-0 h-0 border-t-[6px] border-t-transparent border-b-[6px] border-b-transparent border-l-[6px] border-l-gray-900" />
          </motion.div>
        )}
      </AnimatePresence>

      {/* Mensaje de bienvenida inicial (solo aparece la primera vez) */}
      <AnimatePresence>
        {!isOpen && notificationCount > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 20, scale: 0.8 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.8 }}
            className="absolute bottom-20 right-0 w-64 p-4 bg-white dark:bg-gray-800 rounded-lg shadow-2xl border border-border"
          >
            <div className="flex items-start gap-3">
              <div className="flex-shrink-0 w-10 h-10 rounded-full bg-gradient-to-br from-purple-500 to-blue-600 flex items-center justify-center">
                <Sparkles className="h-5 w-5 text-white" />
              </div>
              <div className="flex-1">
                <h4 className="font-semibold text-sm mb-1">{i18n.t('shell.assistant_hello')}</h4>
                <p className="text-xs text-muted-foreground">
                  {i18n.t('shell.assistant_intro')}
                </p>
              </div>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  // Aquí podrías marcar como visto
                }}
                className="text-muted-foreground hover:text-foreground"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
            <div className="absolute -bottom-2 right-8 w-0 h-0 border-l-[8px] border-l-transparent border-r-[8px] border-r-transparent border-t-[8px] border-t-white dark:border-t-gray-800" />
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
